import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { t } from 'i18next';

import { LOCAL_STORAGE, ONE_DAY_MS, TWELVE_HOURS_MS } from '@app/shared/constants/common';
import { GlassModal, CooldownContainer } from '@app/shared/ui/molecules/Common';
import { ActivityRewardModal, FortuneWheel } from '@app/shared/ui/organisms/Common';
import { SETTLEMENT_DETAILS_CONFIG } from '@app/shared/constants/settlementDetails';
import { AudioPlayer, VideoPlayer } from '@app/shared/ui/organisms/Multimedia';
import { cn, telegramVibrate, toMs } from '@app/shared/utils/common';
import { Button, Image, Paragraph } from '@app/shared/ui/atoms/Common';
import { ModalActions, SettlementItemConfig } from '@app/shared/types/pages/settlementDetails';
import { MediaData } from '@app/shared/types/player';
import {
  calcImageTopPosition,
  getSettlementsDetailsImageUrl,
} from '@app/shared/utils/settlementDetails';
import { MEDIA_LIST } from '@app/shared/constants/media';
import { useMediaCooldown } from '@app/shared/hooks/useMediaCooldown';
import notification from '@app/shared/utils/notification';
import { Page } from '@app/routes';
import ServerErrorPage from '@app/pages/ServerErrorPage';
import { useGetSettlementsQuery } from '@app/store/slices/settlements/settlementsApi';
import { SpinWheelResponse } from '@app/store/slices/users/usersTypes';

export default function SettlementDetailsPage() {
  const navigate = useNavigate();
  const params = useParams();
  const settlementId = params.id ? parseInt(params.id, 10) : undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalActions>();
  const [media, setMedia] = useState<MediaData>();
  const [reward, setReward] = useState<SpinWheelResponse>();
  const { trigger } = useMediaCooldown(LOCAL_STORAGE.details);
  const { data: settlements } = useGetSettlementsQuery();

  const enteredSettlement = settlements?.find((item) => item.id === settlementId);

  if (settlementId === undefined || !enteredSettlement?.isPurchased) {
    return <ServerErrorPage />;
  }

  const pageConfig = SETTLEMENT_DETAILS_CONFIG[settlementId];

  const handleItemClick = (item: SettlementItemConfig) => {
    telegramVibrate();
    const { actionType } = item;

    if (actionType === 'soon') {
      notification.info(t('common.soon'));
    } else if (
      actionType === 'audio' ||
      actionType === 'video' ||
      actionType === 'wheel' ||
      actionType === 'reward'
    ) {
      setModalType(actionType);
      setIsOpen(true);
    }

    if (actionType === 'redirect' && item.route) {
      void navigate(item.route);
      if ((item.route as Page) !== Page.DAILY_ENERGY) {
        trigger(item.route);
      }
    }

    if (item.mediaId && (actionType === 'audio' || actionType === 'video')) {
      const media = MEDIA_LIST[actionType].find((m) => m.id === item.mediaId);
      setMedia(media);
    }
  };

  const handleModalClose = () => {
    setIsOpen(false);
    setMedia(undefined);
  };

  const formatRewardText = (r: SpinWheelResponse) => {
    const { rewardType, rewardAmount } = r;

    if (!rewardType) {
      return '';
    }

    const isCookie = rewardType.startsWith('cookie_');
    const isDiamond = rewardType.startsWith('diamond_');

    if (isCookie) {
      return `${String(rewardAmount)} ${t('wheel_reward_modal.units.cookie')}`.trim();
    }

    if (isDiamond) {
      return `${String(rewardAmount)} ${t('wheel_reward_modal.units.ton')}`.trim();
    }

    return t(`wheel_reward_modal.rewards.${rewardType}`);
  };

  return (
    <div className="relative">
      <div
        className="fixed inset-y-0 left-1/2 -translate-x-1/2 w-[480px] h-full bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${getSettlementsDetailsImageUrl(settlementId)})` }}
      >
        {Object.entries(pageConfig.items).map(([key, value]) => {
          const dynamicTop = calcImageTopPosition(
            window.innerHeight,
            Number(value.top),
            pageConfig.multiplier
          );

          return (
            <CooldownContainer
              key={key}
              itemId={value.mediaId?.toString() ?? value.route ?? key}
              intervalMs={value.actionType === 'wheel' ? ONE_DAY_MS : TWELVE_HOURS_MS}
              storageKey={
                value.actionType === 'audio' || value.actionType === 'video'
                  ? LOCAL_STORAGE.media
                  : LOCAL_STORAGE.details
              }
              onClick={() => {
                handleItemClick(value);
              }}
              className={value.className}
              style={{
                top: `${String(dynamicTop)}px`,
              }}
            >
              <Image src={getSettlementsDetailsImageUrl(settlementId, key)} />
            </CooldownContainer>
          );
        })}

        <GlassModal
          isOpen={isOpen && modalType !== 'reward'}
          onOpenChange={handleModalClose}
          isShadow={false}
          className={cn(modalType === 'wheel' && 'p-0 bg-transparent shadow-none')}
        >
          {media && (
            <>
              {modalType === 'audio' && (
                <AudioPlayer tracks={[media]} settlementId={settlementId} />
              )}
              {modalType === 'video' && <VideoPlayer videos={[media]} />}
            </>
          )}

          {modalType === 'wheel' && (
            <FortuneWheel
              onFinish={(reward) => {
                setReward(reward);
                setModalType('reward');
                trigger('wheel', { lastAt: toMs(reward.spinAt) });
              }}
            />
          )}
        </GlassModal>

        {modalType === 'reward' && reward && (
          <ActivityRewardModal
            isOpen={true}
            setIsOpen={() => {
              setIsOpen(false);
              setReward(undefined);
            }}
            button={
              <Button
                disabled={!reward}
                onClick={() => {
                  setIsOpen(false);
                  setReward(undefined);
                }}
              >
                {t('common.get')}
              </Button>
            }
          >
            <Paragraph className="whitespace-pre-line">
              {t('wheel_reward_modal.title', { reward: formatRewardText(reward) })}
            </Paragraph>
          </ActivityRewardModal>
        )}
      </div>
    </div>
  );
}
